// hooks/useCurrentUser.ts
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuthStore, type AuthUser } from '../store/authStore.js';
import apiClient from '../api/axiosConfig.js';


// ─── Query key factory ────────────────────────────────────────────────────────
export const authKeys = {
  all: ['auth'] as const,
  currentUser: () => [...authKeys.all, 'currentUser'] as const,
};

// ─── Fetcher ──────────────────────────────────────────────────────────────────
const fetchCurrentUser = async (): Promise<AuthUser> => {
  const { data } = await apiClient.get<AuthUser>('/users/profile/');
  return data;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
/**
 * Fetches the current user from the backend and syncs it into Zustand.
 *
 * - Only runs when a token exists (no token = not logged in)
 * - Refetches on window focus so profile_completeness is always fresh
 * - Caches for 5 min so you don't hammer the backend on every navigation
 *
 * Usage: call this once at the app root (e.g. inside AppInitializer or App.tsx).
 * Every other component reads from useAuthStore — zero extra requests.
 */
export const useCurrentUser = () => {
  const { token, setUser, setIsLoading } = useAuthStore();

  const query = useQuery({
    queryKey: authKeys.currentUser(),
    queryFn: fetchCurrentUser,
    enabled: !!token,           // skip entirely when logged out
    staleTime: 5 * 60 * 1000,  // 5 minutes — adjust to taste
    gcTime: 10 * 60 * 1000,    // keep in cache for 10 min after unmount
    refetchOnWindowFocus: true, // ← this solves the stale-data problem
    retry: (failureCount, error: any) => {
      // Don't retry on 401 — the token is bad, trigger logout instead
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });

  // Sync react-query result → Zustand whenever it changes
  useEffect(() => {
    if (query.data) {
      setUser(query.data);
    }
  }, [query.data, setUser]);

  // If query errors with 401, clear the store (expired token etc.)
  useEffect(() => {
    if (query.error) {
      const status = (query.error as any)?.response?.status;
      if (status === 401) {
        useAuthStore.getState().logout();
      } else {
        // Non-auth error: stop the loading spinner but keep whatever user we have
        setIsLoading(false);
      }
    }
  }, [query.error, setIsLoading]);

  return query;
};